import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { StanBadge } from '@/components';
import { BroadcastEditor } from '@/features/email-flows/broadcasts/components/BroadcastEditor';
import { useBroadcastEditorStore } from '@/features/email-flows/broadcasts/stores';
import { useBroadcastEditorData } from '@/features/email-flows/broadcasts/hooks/useBroadcastEditorData';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { useStanRouter } from '@/hooks/useStanRouter';
import { MENUITEM_IDS } from '@/lib';
import { formatEmailSubjectVariables } from '@/features/email-flows/logic/templateVariables.logic';

const EditBroadcastPage: FC = () => {
    const { flowId, emailId } = useParams<{ flowId: string; emailId: string }>();
    const { push } = useStanRouter();

    // Parse and validate flowId and emailId
    const parsedFlowId = Number(flowId) || -1;
    const parsedEmailId = Number(emailId) || -1;

    // Fetch and populate store
    useBroadcastEditorData({ flowId: parsedFlowId });

    // Read from store for display purposes
    const flowData = useBroadcastEditorStore(state => state.flowData);
    const broadcastStatus = useBroadcastEditorStore(state => state.broadcastStatus);
    const resetStore = useBroadcastEditorStore(state => state.reset);

    useEffect(() => {
        if (parsedFlowId === -1 || parsedEmailId === -1) {
            push('/flows');
        }
    }, [parsedFlowId, parsedEmailId, push]);

    // Reset store on unmount
    useEffect(() => {
        return () => resetStore();
    }, [resetStore]);

    useBreadcrumbs([
        { title: 'My Email Flows', to: '/flows', id: MENUITEM_IDS.FLOWS, active: false },
        { title: 'Broadcasts', to: `/flows?tab=broadcasts`, id: 'broadcasts-tab', active: false },
        {
            title: 'Edit Broadcast',
            to: `/flows/${flowId}/broadcast/emails/${emailId}`,
            id: 'edit-broadcast',
            active: true,
        },
    ]);

    // Don't render if IDs are invalid
    if (parsedFlowId === -1 || parsedEmailId === -1) {
        return null;
    }

    const emailData = flowData?.flow_emails?.[0];

    // Determine badge variant based on broadcast status
    const getStatusVariant = () => {
        if (flowData?.status === 'draft') {
            return 'muted';
        }
        if (broadcastStatus?.toLowerCase().includes('sent')) {
            return 'success';
        }
        if (broadcastStatus?.toLowerCase().includes('scheduled')) {
            return 'primary';
        }
        return 'muted';
    };

    const statusVariant = getStatusVariant();
    const formattedSubject = emailData?.data?.subject
        ? formatEmailSubjectVariables(emailData.data.subject)
        : 'Edit Broadcast';

    return (
        <div className='mx-auto w-full max-w-screen-lg pb-6'>
            <div className='mb-6 flex justify-between gap-2'>
                <p className='text-heading-h2'>
                    <span dangerouslySetInnerHTML={{ __html: formattedSubject }} />
                </p>
                {broadcastStatus && (
                    <StanBadge variant={statusVariant} size='md' className='h-fit shrink-0'>
                        {broadcastStatus}
                    </StanBadge>
                )}
            </div>
            <BroadcastEditor flowId={parsedFlowId} emailId={parsedEmailId} />
        </div>
    );
};

export default EditBroadcastPage;
